import React, { useState, useEffect } from 'react'
import { Part, PartType, Model } from '../types'
import { PartsService } from '../services/api'
import {
  PartsContainer,
  PartsTitle,
  SectionTitle,
  PartsTable,
  TableHeader,
  TableCell,
  PartDescription,
  PartTypeBadge,
  ModelsContainer,
  ModelTag,
  UnknownModelTag,
  NoModelsText,
  BooleanIndicator,
  ActionButton,
  HeaderContainer,
  LoadingContainer,
  ErrorContainer
} from './PartsManagement.styles'

const PartsManagement: React.FC = () => {
  // Mock models data - in real app, this would come from an API
  const [models] = useState<Model[]>([
    { id: '1', name: 'Wand Pro', manufacturer: 'TechCorp', category: 'Professional', discontinued: false, createdDate: '', createdBy: '', lastModified: '', lastModifiedBy: '' },
    { id: '2', name: 'Wand Basic', manufacturer: 'TechCorp', category: 'Consumer', discontinued: false, createdDate: '', createdBy: '', lastModified: '', lastModifiedBy: '' },
    { id: '3', name: 'Wand Classic', manufacturer: 'TechCorp', category: 'Legacy', discontinued: true, createdDate: '', createdBy: '', lastModified: '', lastModifiedBy: '' }
  ])

  const [parts, setParts] = useState<Part[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [editingPart, setEditingPart] = useState<Part | null>(null)
  const [formData, setFormData] = useState<Partial<Part>>({
    sku: '',
    name: '',
    description: '',
    costPrice: 0,
    partType: PartType.OTHER,
    bomCompatible: true,
    serialised: false,
    compatibleModels: []
  })

  // Load parts from API
  useEffect(() => {
    loadParts()
  }, [])

  const loadParts = async () => {
    try {
      setLoading(true)
      setError(null)
      const partsData = await PartsService.getAllParts()
      setParts(partsData)
    } catch (err) {
      setError('Failed to load parts. Please try again.')
      console.error('Error loading parts:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      setError(null)
      
      if (editingPart) {
        // Update existing part
        const updatedPart = await PartsService.updatePart(editingPart.id, formData)
        setParts(prev => prev.map(part => 
          part.id === editingPart.id ? updatedPart : part
        ))
      } else {
        // Create new part
        const newPart = await PartsService.createPart({
          sku: formData.sku!,
          name: formData.name!,
          description: formData.description!,
          costPrice: formData.costPrice!,
          partType: formData.partType!,
          bomCompatible: formData.bomCompatible!,
          serialised: formData.serialised!,
          compatibleModels: formData.compatibleModels || []
        })
        setParts(prev => [...prev, newPart])
      }

      // Reset form
      resetForm()
    } catch (err) {
      setError('Failed to save part. Please try again.')
      console.error('Error saving part:', err)
    }
  }

  const handleEdit = (part: Part) => {
    setEditingPart(part)
    setFormData(part)
    setShowForm(true)
  }

  const handleDelete = async (partId: string) => {
    if (confirm('Are you sure you want to delete this part?')) {
      try {
        setError(null)
        await PartsService.deletePart(partId)
        setParts(prev => prev.filter(part => part.id !== partId))
      } catch (err) {
        setError('Failed to delete part. Please try again.')
        console.error('Error deleting part:', err)
      }
    }
  }

  const resetForm = () => {
    setFormData({
      sku: '',
      name: '',
      description: '',
      costPrice: 0,
      partType: PartType.OTHER,
      bomCompatible: true,
      serialised: false,
      compatibleModels: []
    })
    setShowForm(false)
    setEditingPart(null)
    setError(null)
  }

  return (
    <PartsContainer>
      <HeaderContainer>
        <PartsTitle>Parts Management</PartsTitle>
        <button 
          onClick={() => setShowForm(true)} 
          className="btn btn-primary"
          disabled={showForm}
        >
          Add New Part
        </button>
      </HeaderContainer>

      {error && (
        <ErrorContainer>
          {error}
        </ErrorContainer>
      )}

      {loading && (
        <LoadingContainer>
          <p>Loading parts...</p>
        </LoadingContainer>
      )}

      {!loading && (
        <>
          {showForm && (
        <div className="form-section" style={{ marginBottom: '2rem' }}>
          <h3>{editingPart ? 'Edit Part' : 'Add New Part'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="sku">SKU *</label>
                <input
                  type="text"
                  id="sku"
                  className="form-control"
                  value={formData.sku || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="name">Part Name *</label>
                <input
                  type="text"
                  id="name"
                  className="form-control"
                  value={formData.name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                className="form-control"
                rows={3}
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="costPrice">Cost Price *</label>
                <input
                  type="number"
                  step="0.01"
                  id="costPrice"
                  className="form-control"
                  value={formData.costPrice || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, costPrice: parseFloat(e.target.value) }))}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="partType">Part Type *</label>
                <select
                  id="partType"
                  className="form-control"
                  value={formData.partType}
                  onChange={(e) => setFormData(prev => ({ ...prev, partType: e.target.value as PartType }))}
                  required
                >
                  <option value={PartType.WAND}>Wand</option>
                  <option value={PartType.HEAD}>Head</option>
                  <option value={PartType.BATTERY}>Battery</option>
                  <option value={PartType.HOUSING}>Housing</option>
                  <option value={PartType.PROCESSOR}>Processor</option>
                  <option value={PartType.SENSOR}>Sensor</option>
                  <option value={PartType.CABLE}>Cable</option>
                  <option value={PartType.OTHER}>Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.bomCompatible}
                    onChange={(e) => setFormData(prev => ({ ...prev, bomCompatible: e.target.checked }))}
                  />
                  {' '}BOM Compatible
                </label>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.serialised}
                    onChange={(e) => setFormData(prev => ({ ...prev, serialised: e.target.checked }))}
                  />
                  {' '}Serialised
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Compatible Models</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', marginTop: '0.5rem' }}>
                {models.map(model => (
                  <label key={model.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.compatibleModels?.includes(model.id) || false}
                      onChange={(e) => {
                        const currentModels = formData.compatibleModels || [];
                        if (e.target.checked) {
                          setFormData(prev => ({ 
                            ...prev, 
                            compatibleModels: [...currentModels, model.id] 
                          }));
                        } else {
                          setFormData(prev => ({ 
                            ...prev, 
                            compatibleModels: currentModels.filter(id => id !== model.id) 
                          }));
                        }
                      }}
                    />
                    <span>{model.name}</span>
                    <small style={{ color: '#666', marginLeft: 'auto' }}>
                      {model.manufacturer}
                      {model.discontinued && <span style={{ color: '#d32f2f' }}> (Discontinued)</span>}
                    </small>
                  </label>
                ))}
              </div>
              {models.length === 0 && (
                <p style={{ color: '#666', fontStyle: 'italic', margin: '0.5rem 0' }}>
                  No models available. Admin users can create models in Model Management.
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" className="btn btn-primary">
                {editingPart ? 'Update Part' : 'Create Part'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="form-section">
        <SectionTitle>Parts List ({parts.length} parts)</SectionTitle>
        <PartsTable>
          <thead>
            <tr>
              <TableHeader>SKU</TableHeader>
              <TableHeader>Name</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Cost Price</TableHeader>
              <TableHeader>Compatible Models</TableHeader>
              <TableHeader>BOM Compatible</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {parts.map(part => (
              <tr key={part.id}>
                <TableCell><strong>{part.sku}</strong></TableCell>
                <TableCell>
                  <div>{part.name}</div>
                  {part.description && (
                    <PartDescription>
                      {part.description}
                    </PartDescription>
                  )}
                </TableCell>
                <TableCell>
                  <PartTypeBadge>
                    {part.partType.replace('_', ' ').toUpperCase()}
                  </PartTypeBadge>
                </TableCell>
                <TableCell>${part.costPrice.toFixed(2)}</TableCell>
                <TableCell>
                  {part.compatibleModels.length > 0 ? (
                    <ModelsContainer>
                      {part.compatibleModels.map(modelId => {
                        const model = models.find(m => m.id === modelId);
                        return model ? (
                          <ModelTag key={modelId} $discontinued={model.discontinued}>
                            {model.name}
                          </ModelTag>
                        ) : (
                          <UnknownModelTag key={modelId}>
                            Unknown Model
                          </UnknownModelTag>
                        );
                      })}
                    </ModelsContainer>
                  ) : (
                    <NoModelsText>No models specified</NoModelsText>
                  )}
                </TableCell>
                <TableCell>
                  <BooleanIndicator $value={part.bomCompatible}>
                    {part.bomCompatible ? '✓ Yes' : '✗ No'}
                  </BooleanIndicator>
                </TableCell>
                <TableCell>
                  <ActionButton 
                    onClick={() => handleEdit(part)} 
                    $variant="secondary"
                  >
                    Edit
                  </ActionButton>
                  <ActionButton 
                    onClick={() => handleDelete(part.id)} 
                    $variant="danger"
                  >
                    Delete
                  </ActionButton>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </PartsTable>
      </div>
        </>
      )}
    </PartsContainer>
  )
}

export default PartsManagement